/**
 * BlauWeiss Hero-Video (sections/bw-hero.liquid)
 * Setzt nur das passende Video ein (Handy < 750 px: Handy-Video, sonst Desktop-Video; fehlt das
 * Handy-Video, wird das Desktop-Video mittig zugeschnitten). Bei "Bewegung reduzieren" kein Video –
 * dann bleibt das Standbild. Ohne JavaScript ebenfalls Standbild.
 */
const DESKTOP_QUERY = '(min-width: 750px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const RETRY_EVENTS = ['touchstart', 'pointerdown', 'keydown'];

class BwHeroVideo extends HTMLElement {
  connectedCallback() {
    this.desktop = window.matchMedia(DESKTOP_QUERY);
    this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
    this.desktop.addEventListener('change', this.#update);
    this.reducedMotion.addEventListener('change', this.#update);
    this.#update();
  }

  disconnectedCallback() {
    this.desktop?.removeEventListener('change', this.#update);
    this.reducedMotion?.removeEventListener('change', this.#update);
  }

  #update = () => {
    const wanted = this.reducedMotion.matches ? null : this.#templateFor(this.desktop.matches ? 'desktop' : 'mobile');
    if (wanted === this.current) return;

    this.querySelector('video')?.remove();
    this.current = wanted;
    if (!wanted) return;

    const video = wanted.content.querySelector('video')?.cloneNode(true);
    if (!(video instanceof HTMLVideoElement)) return;
    // Hintergrundvideo: stumm, inline, Endlosschleife, sofort laden – Voraussetzung für Autoplay (v. a. iOS)
    video.defaultMuted = true;
    video.muted = true;
    video.autoplay = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('aria-hidden', 'true');
    video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
    this.append(video);
    this.#play(video);
  };

  /** @param {HTMLVideoElement} video */
  #play(video) {
    video.play().catch(() => {
      // Autoplay blockiert (z. B. iPhone-Stromsparmodus): Standbild bleibt, neuer Versuch bei der ersten
      // Berührung/Taste oder wenn der Tab wieder sichtbar wird
      const retry = () => {
        if (video.isConnected && video.paused) video.play().catch(() => {});
        if (!video.paused || !video.isConnected) {
          RETRY_EVENTS.forEach((type) => window.removeEventListener(type, retry));
          document.removeEventListener('visibilitychange', retry);
        }
      };
      RETRY_EVENTS.forEach((type) => window.addEventListener(type, retry, { passive: true }));
      document.addEventListener('visibilitychange', retry);
    });
  }

  /** @param {'desktop' | 'mobile'} kind */
  #templateFor(kind) {
    const own = this.querySelector(`template[data-bw-video="${kind}"]`);
    if (own) return own;
    return this.querySelector(`template[data-bw-video="${kind === 'mobile' ? 'desktop' : 'mobile'}"]`);
  }
}

if (!customElements.get('bw-hero-video')) {
  customElements.define('bw-hero-video', BwHeroVideo);
}

/**
 * Hero-Button ohne eigenen Link: springt zum Abschnitt direkt unter dem Hero (Startseite: Fahrzeug-Wähler),
 * unterhalb eines fixierten Headers.
 */
if (!window.bwHeroNextBound) {
  window.bwHeroNextBound = true;
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-bw-hero-next]') : null;
    const next = link?.closest('.shopify-section')?.nextElementSibling;
    if (!(next instanceof HTMLElement)) return;
    event.preventDefault();
    // Horizon fixiert den Header über die umgebende .header-section (sticky="always" bzw. "scroll-up")
    const header = document.querySelector('#header-component')?.closest('.header-section');
    const sticky = header?.querySelector('#header-component')?.getAttribute('sticky') === 'always';
    const headerOffset = sticky && header ? header.getBoundingClientRect().height : 0;
    window.scrollTo({
      top: next.getBoundingClientRect().top + window.scrollY - headerOffset,
      behavior: window.matchMedia(REDUCED_MOTION_QUERY).matches ? 'auto' : 'smooth',
    });
  });
}
